/**
 * Firing signature — how much a shot gives its shooter away.
 *
 * The minimap already refuses to hand out free intel: a hostile appears only while
 * it is in line of sight, while a recon streak is painting it, or *because it just
 * fired*. That last clause is the one a suppressor is supposed to attack, and until
 * now it was dead code — `Minimap` subscribed to `ai:fire`/`enemy:fire` and nothing
 * in the game ever emitted either, so muzzle flashes revealed nobody.
 *
 * This module is the missing emitter, and it makes the reveal a *value* rather than
 * a fact. Every shot it observes is republished as `combat:shot` carrying the
 * shooter's `signature`, 0..1:
 *
 *   signature 1   -> full reveal, the whole `Minimap.TUNING.fireReveal` window
 *   signature 0.3 -> a brief flicker
 *   signature 0   -> no blip at all; the shot is silent as far as the map is concerned
 *
 * ## Where the shots come from
 *
 * The player's are easy: `weapon:fire`. Hostiles do not publish a fire event of
 * their own — `Enemy._shoot` calls straight into the AI director — but it *does*
 * put a positioned `enemy_fire` on the audio bus, which is a perfectly good "a gun
 * went off here" signal and needs no edit to a file this module does not own. The
 * shooter is resolved back to an actor by proximity to the reported muzzle.
 *
 * ## The other half: what hostiles *hear*
 *
 * The minimap consumer only ever applies to hostiles, because the player is the
 * arrow in the middle of their own map and has no blip to suppress. So on its own
 * it would leave the player's `firingSignature` — the one an attachment actually
 * modifies — with nothing to do, which is precisely the dead-stat problem this
 * whole module exists to avoid.
 *
 * The real player-side consumer is AI hearing. `AIDirector._onPlayerFire` calls
 * `enemy.onSound(origin, 1)` on every soldier in earshot, and `Enemy.onSound`
 * already takes loudness as its second argument, scaling both the audible radius
 * (`hearingRange * loud`) and how precisely the shot is localised. The value that
 * argument wants is the firing signature.
 *
 * `src/ai/` belongs to another agent, so rather than edit it, `_installHearing`
 * intercepts *only* that one call — see the comment there for exactly how, and for
 * why the interception is a no-op at the default signature of 1. The permanent fix
 * is one argument in `AIDirector._onPlayerFire`:
 *
 *     e.onSound(origin, ctx.behaviours?.config?.firingSignature ?? 1);
 *
 * and the day that lands, `_installHearing` should be deleted.
 */

import * as THREE from 'three';

export const TUNING = {
  /**
   * Signature of a hostile's rifle. Hostiles carry no attachments, so this is a
   * constant rather than config — but it goes through exactly the same path as the
   * player's, which is what makes the minimap side of the feature testable.
   */
  hostileSignature: 1,
  /** Max distance from a reported muzzle to the actor that owns it, metres. */
  shooterMatchRadius: 2.6,
  /** Audio ids that mean "a hostile fired". */
  hostileFireSounds: new Set(['enemy_fire']),

  /**
   * Ceiling on the signature.
   *
   * It is deliberately above 1. A muzzle brake buys its recoil control by being
   * *louder* than stock, and a value above 1 has to buy the shooter's opponents
   * something or the modifier is decoration: at 1.25 the minimap holds the blip a
   * quarter longer and hostiles hear the shot a quarter further away. The cap
   * exists because `Enemy.onSound` scales its hearing radius linearly by this and
   * an unbounded value would make one shot alert the entire level.
   */
  maxSignature: 2,
};

const _p = new THREE.Vector3();

export class FiringSignature {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.shots = 0;
    this.revealed = 0;
    this.lastSignature = 1;

    this.heardScaled = 0;

    const ev = ctx.events;
    this._offs = [
      ev.on('weapon:fire', (e) => this._playerShot(e)),
      ev.on('audio:play', (e) => this._maybeHostileShot(e)),
    ];
    this._installHearing();
  }

  /** The player's signature, clamped to the range an attachment may buy. */
  get signature() {
    return clampSig(this.system.config.firingSignature);
  }

  /* ------------------------------- AI hearing ------------------------------- */

  /**
   * Make hostiles hear the player's shot at a volume set by the firing signature.
   *
   * `AIDirector._onPlayerFire` does two separate things with one event: it tells
   * every soldier in earshot that a gun went off (`onSound`), and it applies
   * near-miss suppression to anyone the round passed close to. Only the first of
   * those should care about a suppressor — a bullet cracking past your head is
   * exactly as alarming whether or not there is a can on the muzzle — so the
   * interception has to be surgical.
   *
   * It is done by wrapping, not replacing. The director's own handler still runs
   * and still does all of its own work, including anything added to it later; the
   * only change is that each soldier's `onSound` is parked for the duration of that
   * call and then re-issued once, with the signature as the `loud` argument the
   * method already takes. Nothing about the director's logic is copied here, so
   * there is nothing to drift out of date.
   *
   * At the default signature of exactly 1 — every stock weapon, and every build
   * without a muzzle attachment — the wrapper detects that there is nothing to
   * change and hands straight through, so the common path is one float compare.
   *
   * This is a stand-in for a one-argument change in a file this module does not
   * own; see the header. It restores the original on `dispose`.
   */
  _installHearing() {
    const ai = this.ctx.ai;
    // The AI director is constructed after this module. Retry until it exists,
    // and give up rather than poll forever if the game is running without one.
    if (!ai?._onPlayerFire) {
      if ((this._hearingTries = (this._hearingTries ?? 0) + 1) > 60) return;
      setTimeout(() => this._installHearing(), 250);
      return;
    }
    if (ai.__signatureWrapped) return;

    const original = ai._onPlayerFire.bind(ai);
    this._hearingHost = ai;
    this._originalOnPlayerFire = ai._onPlayerFire;
    const self = this;
    const noop = () => {};

    ai._onPlayerFire = function signatureAwareOnPlayerFire(payload) {
      const sig = self.signature;
      if (sig === 1) return original(payload);

      const list = this.enemies;
      const saved = self._savedSound ?? (self._savedSound = []);
      saved.length = 0;
      for (let i = 0; i < list.length; i++) {
        saved.push(list[i].onSound);
        list[i].onSound = noop;
      }
      try {
        // Near-miss suppression, roles, anything else the director does: untouched.
        original(payload);
      } finally {
        for (let i = 0; i < list.length; i++) list[i].onSound = saved[i];
        saved.length = 0;
      }

      // ...and now the hearing, at the volume this weapon actually makes.
      const origin = payload?.origin;
      if (sig > 0 && origin) {
        for (let i = 0; i < list.length; i++) {
          const e = list[i];
          if (!e.dead) e.onSound(origin, sig);
        }
        self.heardScaled++;
      }
      return undefined;
    };
    ai.__signatureWrapped = true;
    this.hearingWrapped = true;
  }

  /* -------------------------------- player --------------------------------- */

  _playerShot(e) {
    if (!e) return;
    const sig = this.signature;
    this.lastSignature = sig;
    this.shots++;
    const p = this.ctx.player;
    const pos = e.muzzle ?? e.origin ?? p?.position;
    this.ctx.events.emit('combat:shot', {
      actor: p ?? null,
      position: pos ?? null,
      signature: sig,
      isPlayer: true,
    });
  }

  /* -------------------------------- hostiles -------------------------------- */

  _maybeHostileShot(e) {
    if (!e || !TUNING.hostileFireSounds.has(e.id) || !e.position) return;
    const actor = this._shooterAt(e.position);
    if (!actor) return;
    this.shots++;
    this.revealed++;
    this.ctx.events.emit('combat:shot', {
      actor,
      position: e.position,
      // Per-actor rather than global: the day hostiles carry loadouts, this reads
      // `actor.firingSignature` and nothing else has to change.
      signature: clampSig(actor.firingSignature ?? TUNING.hostileSignature),
      isPlayer: false,
    });
  }

  /** The living hostile whose muzzle is closest to `pos`, within a tight radius. */
  _shooterAt(pos) {
    const list = this.ctx.ai?.enemies;
    if (!Array.isArray(list)) return null;
    let best = null;
    let bestD = TUNING.shooterMatchRadius * TUNING.shooterMatchRadius;
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (!a || a.dead || !a.position) continue;
      // The muzzle sits about chest height and half a metre forward; comparing
      // against the feet position would need a radius wide enough to confuse two
      // soldiers standing together.
      _p.set(a.position.x, a.position.y + 1.2, a.position.z);
      const d = _p.distanceToSquared(pos);
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    return best;
  }

  report() {
    return {
      shots: this.shots,
      signature: this.signature,
      lastSignature: this.lastSignature,
      /** False means hostiles are hearing every shot at stock volume regardless
       *  of what the muzzle attachment claims — i.e. the suppressor is decorative. */
      hearingWrapped: !!this.hearingWrapped,
      /** Shots whose audible radius was actually rescaled. */
      heardScaled: this.heardScaled,
    };
  }

  dispose() {
    for (const off of this._offs) off();
    this._offs.length = 0;
    const ai = this._hearingHost;
    if (ai && this._originalOnPlayerFire) {
      ai._onPlayerFire = this._originalOnPlayerFire;
      ai.__signatureWrapped = false;
    }
  }
}

function clampSig(v) {
  if (!Number.isFinite(v)) return 1;
  return v < 0 ? 0 : v > TUNING.maxSignature ? TUNING.maxSignature : v;
}
